/**
 * Tour Planning Preferences card — shown in the voice assistant settings
 * panel for admin and sales_rep users.
 *
 * Lets the user set their personal max drive hours per leg and days off
 * per week. These values are loaded by the voice assistant on session
 * startup and injected into the system prompt so the assistant always
 * knows the user's tour planning constraints without being told every time.
 */
import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Loader2, MapPin, Save } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type TourPrefs = {
  tourMaxDriveHours: number;
  tourDaysOffPerWeek: number;
};

export function TourPreferencesCard() {
  const { toast } = useToast();

  const { data, isLoading } = useQuery<TourPrefs>({
    queryKey: ["/api/user/tour-preferences"],
  });

  const [maxDriveHours, setMaxDriveHours] = useState(6);
  const [daysOffPerWeek, setDaysOffPerWeek] = useState(1);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setMaxDriveHours(data.tourMaxDriveHours ?? 6);
      setDaysOffPerWeek(data.tourDaysOffPerWeek ?? 1);
      setDirty(false);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: () =>
      apiRequest("PATCH", "/api/user/tour-preferences", {
        tourMaxDriveHours: maxDriveHours,
        tourDaysOffPerWeek: daysOffPerWeek,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/tour-preferences"] });
      setDirty(false);
      toast({ title: "Tour preferences saved" });
    },
    onError: () => {
      toast({
        title: "Failed to save",
        description: "Could not update tour preferences.",
        variant: "destructive",
      });
    },
  });

  const handleMaxDriveChange = (val: number[]) => {
    setMaxDriveHours(val[0]);
    setDirty(true);
  };

  const handleDaysOffChange = (val: number[]) => {
    setDaysOffPerWeek(val[0]);
    setDirty(true);
  };

  return (
    <Card data-testid="tour-preferences-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          Tour Planning Preferences
        </CardTitle>
        <CardDescription>
          The voice assistant uses these to plan drives, size markets, and sketch itineraries without asking you every time.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : (
          <>
            {/* Max drive hours per leg */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label data-testid="label-max-drive-hours" className="text-sm font-medium">
                  Max drive per leg
                </Label>
                <span
                  data-testid="value-max-drive-hours"
                  className="text-sm font-semibold tabular-nums"
                >
                  {maxDriveHours}h
                </span>
              </div>
              <Slider
                data-testid="slider-max-drive-hours"
                min={1}
                max={12}
                step={1}
                value={[maxDriveHours]}
                onValueChange={handleMaxDriveChange}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                The assistant will flag any proposed leg that exceeds this limit and offer to insert a travel day.
              </p>
            </div>

            {/* Days off per week */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label data-testid="label-days-off-per-week" className="text-sm font-medium">
                  Days off per week
                </Label>
                <span
                  data-testid="value-days-off-per-week"
                  className="text-sm font-semibold tabular-nums"
                >
                  {daysOffPerWeek === 0 ? "None" : `${daysOffPerWeek} day${daysOffPerWeek > 1 ? "s" : ""}`}
                </span>
              </div>
              <Slider
                data-testid="slider-days-off-per-week"
                min={0}
                max={3}
                step={1}
                value={[daysOffPerWeek]}
                onValueChange={handleDaysOffChange}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                When sketching multi-week tours, the assistant will automatically include this many rest days per 7-day block.
              </p>
            </div>

            <Button
              data-testid="button-save-tour-preferences"
              size="sm"
              disabled={!dirty || mutation.isPending}
              onClick={() => mutation.mutate()}
              className="w-full sm:w-auto"
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save preferences
                </>
              )}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
